import { storePath } from '../address.js';
import { ApiError } from '@arabat/api-client';
import { Alert, Button, Checkbox, Field, Input, Select, Spinner, Textarea, cn } from '@arabat/ui';
import { useApi, useErrorMessage, useMoneyFormat } from '@arabat/web-core';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { type FormEvent, type ReactNode, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, Navigate, useNavigate } from 'react-router';
import {
  LineChoices,
  checkoutLine,
  lineKey,
  priceLines,
  sampleLines,
  toMoney,
  useCart,
} from '../cart.js';
import { isPreview, linkTarget, useDesign, useText } from '../design/context.js';
import { StoreIcon } from '../design/parts.js';
import { Highlights } from '../design/product-parts.js';
import { AreaPicker } from './AreaPicker.js';
import { SlotPicker } from './SlotPicker.js';
import {
  useAccount,
  useBranchChoice,
  useDeliveryChoice,
  useLocalName,
  useSlug,
  useStoreProducts,
  useStorefront,
  useStorePaths,
} from '../shared.js';

const newKey = () => globalThis.crypto.randomUUID();

/**
 * Checkout: the server re-prices the cart and either reserves the stock and starts the
 * payment (the shopper is then sent to the gateway's page) or, when paying on receipt, places
 * the order for the store to confirm. The idempotency key is kept across retries of the same
 * submission (network errors) and renewed when the server refused it.
 */
const ON_RECEIPT = 'on_receipt';

/** A pickup / delivery or payment choice: a large card or a plain line (the design decides). */
function Choice({ cards, children }: { cards: boolean; children: ReactNode }) {
  return (
    <label
      className={cn(
        'flex cursor-pointer items-center gap-2',
        cards &&
          'min-h-12 rounded-lg border border-border bg-surface px-4 py-3 font-medium transition-colors hover:border-border-strong has-[:checked]:border-primary has-[:checked]:bg-primary-soft has-[:checked]:ring-1 has-[:checked]:ring-primary',
      )}
    >
      {children}
    </label>
  );
}

/** A part of the form, numbered when the design shows steps. */
function Step({ n, steps, children }: { n: number; steps: boolean; children: ReactNode }) {
  return (
    <legend className="mb-2 flex items-center gap-2 font-semibold">
      {steps ? (
        <span
          aria-hidden
          className="grid size-7 place-items-center rounded-full bg-primary text-sm text-primary-fg"
        >
          {n}
        </span>
      ) : null}
      {children}
    </legend>
  );
}

/** The checkout, drawn by the store design's checkout page (ADR-0128 amendment 5). */
export function CheckoutPage() {
  const paths = useStorePaths();
  const api = useApi();
  const slug = useSlug();
  const cart = useCart();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { data: store } = useStorefront();
  const products = useStoreProducts();
  const name = useLocalName();
  const money = useMoneyFormat();
  const errorMessage = useErrorMessage();
  const { t } = useTranslation();
  const page = useDesign().checkoutPage;
  const text = useText();
  const preview = isPreview();
  const [key, setKey] = useState(newKey);
  const [form, setForm] = useState({
    /** Null = not typed yet: a signed-in shopper's account value shows instead (ADR-0124). */
    name: null as string | null,
    phone: null as string | null,
    email: null as string | null,
    fulfillment: null as 'pickup' | 'delivery' | null,
    deliveryAddress: null as string | null,
    /** Delivery area (ADR-0123); required when the store defines areas. */
    areaId: null as string | null,
    /** The governorate area within it (ADR-0140); required when the zone covers places. */
    placeId: null as string | null,
    /** Requested slot start, or null for "as soon as possible". */
    scheduledFor: null as string | null,
    note: '',
    /** A gateway method id, `ON_RECEIPT`, or null until chosen. */
    payment: null as string | null,
    /** Promotion code typed by the shopper (ADR-0125); applied through the quote. */
    promoInput: '',
    promotionCode: null as string | null,
    /** A saved address of the account (ADR-0124): null = untouched (the default one), '' = a new one. */
    savedAddressId: null as string | null,
    saveAddress: false,
    terms: false,
  });
  const [touched, setTouched] = useState(false);
  const { branch: pickupBranch, setBranchId } = useBranchChoice();
  // The area or pickup chosen on entering the store (ADR-0141) starts the checkout.
  const { choice, setChoice } = useDeliveryChoice();
  const chosenArea = choice?.mode === 'delivery' ? choice : null;
  const { token, account, addresses } = useAccount();
  // A signed-in shopper's details and default address show until they type something else.
  const defaultAddress = addresses.find((a) => a.isDefault) ?? addresses[0] ?? null;
  const savedAddressId = form.savedAddressId ?? defaultAddress?.id ?? '';
  const savedAddress = addresses.find((a) => a.id === savedAddressId) ?? null;
  const values = {
    name: form.name ?? account?.fullName ?? '',
    phone: form.phone ?? account?.phone ?? '',
    email: form.email ?? account?.email ?? '',
    deliveryAddress: form.deliveryAddress ?? savedAddress?.address ?? '',
    areaId: form.areaId ?? chosenArea?.areaId ?? savedAddress?.deliveryAreaId ?? null,
    placeId: form.areaId
      ? form.placeId
      : chosenArea
        ? chosenArea.placeId
        : (savedAddress?.deliveryPlaceId ?? null),
  };

  const checkout = useMutation({
    mutationFn: (body: Parameters<typeof api.store.checkout>[1]) =>
      api.store.checkout(slug, body, token),
    onSuccess: ({ order }) => {
      // Leave the page before emptying the cart, or the empty cart would send us to the cart page.
      if (order.paymentUrl) window.location.assign(order.paymentUrl);
      else void navigate(storePath(slug, `/orders/${order.trackingToken}`), { replace: true });
      cart.clear();
    },
    onError: (error) => {
      if (error instanceof ApiError && error.status < 500) {
        setKey(newKey());
        void queryClient.invalidateQueries({ queryKey: ['store', slug, 'products'] });
      }
    },
  });

  // Nothing is chosen for the shopper, even with one way only (owner, 2026-10-26), unless they
  // already answered on entering the store (ADR-0141).
  const askedOnEntry = store?.areaPrompt === 'entry';
  const chosen: 'pickup' | 'delivery' | null =
    form.fulfillment ??
    (askedOnEntry && chosenArea && store.deliveryEnabled
      ? 'delivery'
      : askedOnEntry && choice?.mode === 'pickup' && store.pickupEnabled
        ? 'pickup'
        : null);
  // Prices and the quote count no delivery fee until delivery is chosen.
  const fulfillment = chosen ?? 'pickup';
  const deliveryAreas = store?.deliveryAreas ?? [];
  // A picked place names its zone (ADR-0140); a zone with places needs one of them picked.
  const zone =
    fulfillment === 'delivery'
      ? (deliveryAreas.find((a) =>
          values.placeId ? a.places.some((p) => p.id === values.placeId) : a.id === values.areaId,
        ) ?? null)
      : null;
  const area = zone && (zone.places.length === 0 || values.placeId) ? zone : null;
  const placeId = area && area.places.length > 0 ? values.placeId : null;
  // The area's branch may hold other stock than the one browsed (ADR-0141): check the cart there.
  const areaBranchId = area && area.branchId !== pickupBranch?.id ? area.branchId : null;
  const areaStock = useQuery({
    queryKey: ['store', slug, 'products', areaBranchId, 'area'],
    queryFn: () => api.store.products(slug, areaBranchId),
    enabled: areaBranchId !== null && !preview,
    staleTime: 15_000,
  });
  // Once the area is chosen the page's own list is usually that branch's already.
  const areaProducts = areaBranchId ? areaStock.data : area ? products.data : undefined;
  const unavailable = areaProducts
    ? cart.lines.filter((l) => {
        const v = areaProducts.products
          .flatMap((p) => p.variants)
          .find((x) => x.variantId === l.variantId);
        return !v || !v.available || v.maxQuantity < l.quantity;
      })
    : [];
  // Inside the console's preview an empty cart shows two sample products (never ordered).
  const sample =
    preview && cart.lines.length === 0 && products.data
      ? sampleLines(products.data.products)
      : null;
  const { priced, subtotal, deliveryFee, total } = priceLines(
    sample ?? cart.lines,
    products.data?.products ?? [],
    store ?? { deliveryFee: { amount: '0.000', currency: 'KWD' } },
    fulfillment,
    deliveryAreas.length > 0 ? area : undefined,
  );
  const set = (patch: Partial<typeof form>) => setForm((f) => ({ ...f, ...patch }));
  // The server's totals with the promotion that applies (ADR-0125).
  const quote = useQuery({
    queryKey: [
      'store',
      slug,
      'quote',
      priced.map((l) => `${l.variantId}:${l.quantity}`).join(','),
      fulfillment,
      area?.id ?? null,
      placeId,
      pickupBranch?.id ?? null,
      form.promotionCode,
      token,
    ],
    enabled: priced.length > 0,
    staleTime: 30_000,
    queryFn: () =>
      api.store.quote(
        slug,
        {
          lines: priced.map(checkoutLine),
          fulfillment,
          deliveryAreaId: area?.id ?? null,
          deliveryPlaceId: placeId,
          branchId: fulfillment === 'pickup' ? (pickupBranch?.id ?? null) : null,
          promotionCode: form.promotionCode,
        },
        token,
      ),
  });
  const quoted = quote.data ?? null;
  const shownDeliveryFee = quoted ? toMoney(quoted.deliveryFee) : deliveryFee;
  const shownDiscount = quoted ? toMoney(quoted.discount) : null;
  const shownTotal = quoted ? toMoney(quoted.total) : total;

  if (!store || products.isPending) return <Spinner label={t('common:status.loading')} />;
  if (!products.data || (cart.lines.length === 0 && !checkout.isSuccess && !sample))
    return <Navigate to={storePath(slug, `/cart`)} replace />;

  const payment =
    form.payment ?? store.paymentMethods[0]?.id ?? (store.payOnReceipt ? ON_RECEIPT : null);
  const payOnReceipt = payment === ON_RECEIPT;
  const missing = {
    name: !values.name.trim(),
    phone: values.phone.trim().length < 6,
    email: page.email === 'required' && !/^\S+@\S+\.\S+$/.test(values.email.trim()),
    terms: page.terms.enabled && !form.terms,
    fulfillment: chosen === null,
    address: fulfillment === 'delivery' && !values.deliveryAddress.trim(),
    area: fulfillment === 'delivery' && store.deliveryAreas.length > 0 && area === null,
  };
  // The branch that prepares the order (ADR-0141): the pickup branch, or the one that delivers
  // to the area. It is known for delivery only once the area is; the shopper never sees it.
  const needsArea = fulfillment === 'delivery' && store.deliveryAreas.length > 0;
  const branch =
    fulfillment === 'delivery'
      ? needsArea
        ? area
          ? (store.branches.find((b) => b.id === area.branchId) ?? null)
          : null
        : (store.branches.find((b) => b.deliveryEnabled) ?? null)
      : pickupBranch;
  const pickupBranches = store.branches.filter((b) => b.pickupEnabled);
  const openNow = branch
    ? fulfillment === 'delivery'
      ? branch.deliveryOpenNow
      : branch.openNow
    : true;
  const closedNow = branch !== null && !openNow;
  const bookable = branch?.scheduling.enabled ?? false;
  // Delivery times wait for the area: they are the times of its branch.
  const waitingArea =
    needsArea && area === null && store.branches.some((b) => b.scheduling.enabled);

  const submit = (event: FormEvent) => {
    event.preventDefault();
    setTouched(true);
    if (missing.name || missing.phone || missing.fulfillment || missing.address || !payment) return;
    if (missing.area || missing.email || missing.terms) return;
    // The preview shows the page only: nothing is ordered from it.
    if (preview) return;
    checkout.mutate({
      idempotencyKey: key,
      lines: priced.map(checkoutLine),
      fulfillment,
      customer: {
        name: values.name,
        phone: values.phone,
        email: page.email === 'hidden' ? null : values.email || null,
      },
      deliveryAddress: fulfillment === 'delivery' ? values.deliveryAddress : null,
      branchId: fulfillment === 'pickup' ? (pickupBranch?.id ?? null) : null,
      deliveryAreaId: area?.id ?? null,
      deliveryPlaceId: placeId,
      scheduledFor: form.scheduledFor,
      note: page.note.enabled ? form.note || null : null,
      promotionCode: quoted?.promotion?.code ?? null,
      saveAddress: Boolean(token) && form.saveAddress && savedAddressId === '',
      paymentMethodId: payOnReceipt ? null : payment,
      expectedTotal: shownTotal.toDecimalString(),
    });
  };
  const required = (bad: boolean) => (touched && bad ? t('checkout.required') : undefined);
  const cards = page.choices === 'cards';
  const showWhen = chosen !== null && (waitingArea || bookable || closedNow);
  const termsLink = linkTarget(slug, page.terms.link, paths);
  const submitLabel =
    text(page.submitLabel) || (payOnReceipt ? t('checkout.submitOnReceipt') : t('checkout.submit'));

  return (
    <form
      onSubmit={submit}
      className={cn(
        'grid grid-cols-[minmax(0,1fr)] gap-6',
        page.layout === 'split' ? 'md:grid-cols-[minmax(0,1fr)_20rem]' : 'mx-auto w-full max-w-2xl',
      )}
      data-testid="checkout-page"
      data-layout={page.layout}
      noValidate
    >
      <div className="flex min-w-0 flex-col gap-6">
        <h1 className="text-2xl font-semibold">{t('checkout.title')}</h1>
        {preview ? <Alert tone="info">{t('checkout.previewOnly')}</Alert> : null}
        {text(page.message) ? (
          <p
            className="rounded-lg bg-primary-soft px-4 py-3 text-sm"
            data-testid="checkout-message"
          >
            {text(page.message)}
          </p>
        ) : null}
        <fieldset className="flex min-w-0 flex-col gap-3">
          <Step n={1} steps={page.steps}>
            {t('checkout.contact')}
          </Step>
          {store.accountsEnabled && !token ? (
            <p className="text-sm text-fg-muted">
              <Link to={storePath(slug, `/account`)} className="text-primary hover:underline">
                {t('nav.signIn')}
              </Link>{' '}
              {t('account.signInHint')}
            </p>
          ) : null}
          <Field label={t('checkout.name')} error={required(missing.name)}>
            {(p) => (
              <Input
                {...p}
                autoComplete="name"
                value={values.name}
                onChange={(e) => set({ name: e.target.value })}
              />
            )}
          </Field>
          <Field label={t('checkout.phone')} error={required(missing.phone)}>
            {(p) => (
              <Input
                {...p}
                type="tel"
                autoComplete="tel"
                dir="ltr"
                value={values.phone}
                onChange={(e) => set({ phone: e.target.value })}
              />
            )}
          </Field>
          {page.email !== 'hidden' ? (
            <Field
              label={page.email === 'required' ? t('checkout.emailRequired') : t('checkout.email')}
              error={required(missing.email)}
            >
              {(p) => (
                <Input
                  {...p}
                  type="email"
                  autoComplete="email"
                  dir="ltr"
                  value={values.email}
                  onChange={(e) => set({ email: e.target.value })}
                />
              )}
            </Field>
          ) : null}
        </fieldset>
        <fieldset className="flex min-w-0 flex-col gap-2">
          <Step n={2} steps={page.steps}>
            {t('checkout.fulfillment')}
          </Step>
          {store.pickupEnabled ? (
            <Choice cards={cards}>
              <input
                type="radio"
                name="fulfillment"
                className="accent-primary"
                checked={chosen === 'pickup'}
                onChange={() => set({ fulfillment: 'pickup', scheduledFor: null })}
              />
              {cards ? <StoreIcon name="store" className="size-5 text-fg-muted" /> : null}
              {t('checkout.pickup')}
            </Choice>
          ) : null}
          {chosen === 'pickup' && pickupBranches.length > 1 ? (
            <Field label={t('checkout.branch')}>
              {(p) => (
                <Select
                  {...p}
                  value={pickupBranch?.id ?? ''}
                  onChange={(e) => setBranchId(e.target.value || null)}
                >
                  {pickupBranches.map((b) => (
                    <option key={b.id} value={b.id}>
                      {[name(b), b.address].filter(Boolean).join(' — ')}
                    </option>
                  ))}
                </Select>
              )}
            </Field>
          ) : chosen === 'pickup' && branch?.address ? (
            <p className="text-sm text-fg-muted">{[name(branch), branch.address].join(' — ')}</p>
          ) : null}
          {store.deliveryEnabled ? (
            <Choice cards={cards}>
              <input
                type="radio"
                name="fulfillment"
                className="accent-primary"
                checked={chosen === 'delivery'}
                onChange={() => set({ fulfillment: 'delivery', scheduledFor: null })}
              />
              {cards ? <StoreIcon name="pin" className="size-5 text-fg-muted" /> : null}
              {t('checkout.delivery')}
              {store.deliveryAreas.length === 0 ? ` (${money(store.deliveryFee)})` : ''}
            </Choice>
          ) : null}
          {touched && missing.fulfillment ? (
            <p className="text-sm text-danger" data-testid="fulfillment-required">
              {t('checkout.chooseFulfillment')}
            </p>
          ) : null}
          {fulfillment === 'delivery' && store.deliveryAreas.length > 0 ? (
            <AreaPicker
              zones={store.deliveryAreas}
              regions={store.deliveryRegions}
              value={{ areaId: values.areaId, placeId: values.placeId }}
              onChange={(picked) => {
                set({ ...picked, scheduledFor: null });
                setChoice({ mode: 'delivery', ...picked });
              }}
              error={required(missing.area)}
            />
          ) : null}
          {unavailable.length > 0 ? (
            <Alert tone="warning" data-testid="unavailable-for-area">
              <ul className="flex flex-col gap-2">
                {unavailable.map((l) => {
                  const product = products.data.products.find((p) => p.productId === l.productId);
                  return (
                    <li key={lineKey(l)} className="flex items-center justify-between gap-3">
                      <span>
                        {t('checkout.notForArea', { name: product ? name(product) : '' })}
                      </span>
                      <Button size="sm" variant="secondary" onClick={() => cart.remove(lineKey(l))}>
                        {t('checkout.removeItem')}
                      </Button>
                    </li>
                  );
                })}
              </ul>
            </Alert>
          ) : null}
          {area?.freeDeliveryFrom ? (
            <p className="text-sm text-fg-muted">
              {t('checkout.freeFrom', { amount: money(area.freeDeliveryFrom) })}
            </p>
          ) : null}
          {fulfillment === 'delivery' && token && addresses.length > 0 ? (
            <Field label={t('account.useSaved')}>
              {(p) => (
                <Select
                  {...p}
                  value={savedAddressId}
                  onChange={(e) => {
                    const chosen = addresses.find((a) => a.id === e.target.value) ?? null;
                    set({
                      savedAddressId: e.target.value,
                      deliveryAddress: chosen ? chosen.address : '',
                      areaId: chosen?.deliveryAreaId ?? null,
                      placeId: chosen?.deliveryPlaceId ?? null,
                    });
                  }}
                >
                  {addresses.map((a) => (
                    <option key={a.id} value={a.id}>
                      {[a.label, a.address].filter(Boolean).join(' — ')}
                    </option>
                  ))}
                  <option value="">{t('account.newAddress')}</option>
                </Select>
              )}
            </Field>
          ) : null}
          {fulfillment === 'delivery' ? (
            <Field label={t('checkout.address')} error={required(missing.address)}>
              {(p) => (
                <Textarea
                  {...p}
                  autoComplete="street-address"
                  value={values.deliveryAddress}
                  onChange={(e) => set({ deliveryAddress: e.target.value, savedAddressId: '' })}
                />
              )}
            </Field>
          ) : null}
          {fulfillment === 'delivery' && token && savedAddressId === '' ? (
            <Checkbox
              label={t('account.saveAddress')}
              checked={form.saveAddress}
              onChange={(e) => set({ saveAddress: e.target.checked })}
            />
          ) : null}
          {page.note.enabled ? (
            <Field label={text(page.note.label) || t('checkout.note')}>
              {(p) => (
                <Textarea
                  {...p}
                  value={form.note}
                  onChange={(e) => set({ note: e.target.value })}
                />
              )}
            </Field>
          ) : null}
        </fieldset>
        {showWhen ? (
          <fieldset className="flex min-w-0 flex-col gap-2">
            <Step n={3} steps={page.steps}>
              {t('checkout.when')}
            </Step>
            {waitingArea ? (
              <p className="text-sm text-fg-muted" data-testid="when-after-area">
                {t('checkout.whenAfterArea')}
              </p>
            ) : null}
            {closedNow ? (
              <Alert tone="warning">
                {t(
                  `checkout.${fulfillment === 'delivery' ? 'deliveryClosedNow' : 'closedNow'}${bookable ? '' : 'Later'}`,
                )}
              </Alert>
            ) : null}
            {bookable && branch ? (
              <SlotPicker
                branchId={branch.id}
                fulfillment={fulfillment}
                value={form.scheduledFor}
                onChange={(scheduledFor) => set({ scheduledFor })}
                asapAllowed={!closedNow}
              />
            ) : null}
          </fieldset>
        ) : null}
        <fieldset className="flex min-w-0 flex-col gap-2">
          <Step n={showWhen ? 4 : 3} steps={page.steps}>
            {t('checkout.payment')}
          </Step>
          {store.paymentMethods.length === 0 && !store.payOnReceipt ? (
            <Alert tone="warning">{t('checkout.noMethods')}</Alert>
          ) : null}
          {store.paymentMethods.map((m) => (
            <Choice key={m.id} cards={cards}>
              <input
                type="radio"
                name="payment"
                className="accent-primary"
                checked={payment === m.id}
                onChange={() => set({ payment: m.id })}
              />
              {cards ? <StoreIcon name="card" className="size-5 text-fg-muted" /> : null}
              {name(m)}
            </Choice>
          ))}
          {store.payOnReceipt ? (
            <Choice cards={cards}>
              <input
                type="radio"
                name="payment"
                className="accent-primary"
                checked={payOnReceipt}
                onChange={() => set({ payment: ON_RECEIPT })}
              />
              {cards ? <StoreIcon name="bag" className="size-5 text-fg-muted" /> : null}
              {t(`checkout.payOnReceipt_${fulfillment}`)}
            </Choice>
          ) : null}
          {payOnReceipt ? (
            <p className="text-sm text-fg-muted">{t('checkout.payOnReceiptHint')}</p>
          ) : null}
        </fieldset>
      </div>
      <aside
        className={cn(
          'flex h-fit flex-col gap-3 rounded-lg border border-border bg-surface p-4',
          page.layout === 'split' && 'md:sticky md:top-24',
        )}
      >
        <h2 className="font-semibold">{t('checkout.summary')}</h2>
        <ul className="flex flex-col gap-1 text-sm">
          {priced.map((l) => (
            <li key={l.key} className="flex items-center justify-between gap-2">
              {page.images ? (
                <span className="grid size-10 shrink-0 place-items-center overflow-hidden rounded-md bg-surface-muted text-fg-subtle">
                  {l.product.imageUrl ? (
                    <img src={l.product.imageUrl} alt="" className="size-full object-cover" />
                  ) : (
                    <StoreIcon name="bag" className="size-4 opacity-50" />
                  )}
                </span>
              ) : null}
              <span className="min-w-0 flex-1">
                {l.quantity} × {[name(l.product), name(l.variant)].filter(Boolean).join(' — ')}
                <LineChoices line={l} />
              </span>
              <span className="tabular-nums">{money(l.lineTotal)}</span>
            </li>
          ))}
        </ul>
        {page.promo ? (
          <div className="flex flex-col gap-1">
            <Field label={t('checkout.promoCode')}>
              {(p) => (
                <div className="flex gap-2">
                  <Input
                    {...p}
                    dir="ltr"
                    autoComplete="off"
                    value={form.promoInput}
                    onChange={(e) => set({ promoInput: e.target.value.toUpperCase() })}
                  />
                  <Button
                    type="button"
                    variant="secondary"
                    onClick={() => set({ promotionCode: form.promoInput.trim() || null })}
                  >
                    {t('checkout.apply')}
                  </Button>
                </div>
              )}
            </Field>
            {quoted?.codeIssue ? (
              <p className="text-sm text-danger" data-testid="promo-issue">
                {t(`checkout.codeIssue_${quoted.codeIssue}`)}
              </p>
            ) : quoted?.promotion ? (
              <p className="text-sm text-success" data-testid="promo-applied">
                {t('checkout.promoApplied', { name: name(quoted.promotion) })}
              </p>
            ) : null}
          </div>
        ) : quoted?.promotion ? (
          <p className="text-sm text-success" data-testid="promo-applied">
            {t('checkout.promoApplied', { name: name(quoted.promotion) })}
          </p>
        ) : null}
        <dl className="flex flex-col gap-1 border-t border-border pt-2 text-sm">
          <div className="flex justify-between">
            <dt>{t('cart.subtotal')}</dt>
            <dd className="tabular-nums">{money(subtotal)}</dd>
          </div>
          {shownDiscount && !shownDiscount.isZero() ? (
            <div className="flex justify-between">
              <dt>{t('checkout.discount')}</dt>
              <dd className="tabular-nums" data-testid="checkout-discount">
                -{money(shownDiscount)}
              </dd>
            </div>
          ) : null}
          {fulfillment === 'delivery' ? (
            <div className="flex justify-between">
              <dt>{t('checkout.deliveryFee')}</dt>
              <dd className="tabular-nums">{money(shownDeliveryFee)}</dd>
            </div>
          ) : null}
          <div className="flex justify-between text-base font-semibold">
            <dt>{t('checkout.total')}</dt>
            <dd className="tabular-nums" data-testid="checkout-total">
              {money(shownTotal)}
            </dd>
          </div>
        </dl>
        {page.terms.enabled ? (
          <div className="flex flex-col gap-1">
            <Checkbox
              label={text(page.terms.text) || t('checkout.terms')}
              checked={form.terms}
              onChange={(e) => set({ terms: e.target.checked })}
            />
            {termsLink ? (
              termsLink.external ? (
                <a
                  href={termsLink.to}
                  target="_blank"
                  rel="noreferrer"
                  className="text-xs text-primary hover:underline"
                >
                  {t('checkout.readTerms')}
                </a>
              ) : (
                <Link to={termsLink.to} className="text-xs text-primary hover:underline">
                  {t('checkout.readTerms')}
                </Link>
              )
            ) : null}
            {touched && missing.terms ? (
              <p className="text-sm text-danger">{t('checkout.termsRequired')}</p>
            ) : null}
          </div>
        ) : null}
        {checkout.isError ? <Alert tone="danger">{errorMessage(checkout.error)}</Alert> : null}
        <Button
          type="submit"
          size="lg"
          disabled={
            preview ||
            checkout.isPending ||
            !store.acceptingOrders ||
            !payment ||
            unavailable.length > 0 ||
            (closedNow && form.scheduledFor === null)
          }
        >
          {submitLabel}
        </Button>
        {!payOnReceipt ? <p className="text-xs text-fg-muted">{t('checkout.reserved')}</p> : null}
        <Highlights items={page.highlights} testId="checkout-highlights" />
      </aside>
    </form>
  );
}
